import { useEffect, useRef, useState } from 'react'
import {
  Box, CircularProgress, IconButton, InputAdornment, Stack, TextField, Tooltip, Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import MyLocationIcon from '@mui/icons-material/MyLocation'
import CloseIcon from '@mui/icons-material/Close'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

const MARKER_ICON = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
})

interface Props {
  latitude: number | null
  longitude: number | null
  onChange: (lat: number, lng: number, address?: string) => void
  height?: number
}

export function MapPicker({ latitude, longitude, onChange, height = 360 }: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<L.Map | null>(null)
  const markerRef = useRef<L.Marker | null>(null)

  const [query, setQuery] = useState('')
  const [searching, setSearching] = useState(false)
  const [locating, setLocating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const defaultCenter: L.LatLngExpression = latitude && longitude
    ? [latitude, longitude]
    : [20, 0]
  const defaultZoom = latitude && longitude ? 15 : 2

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return

    const map = L.map(containerRef.current, {
      center: defaultCenter,
      zoom: defaultZoom,
      zoomControl: false,
      attributionControl: false,
    })

    // Google-style satellite hybrid — vibrant, high contrast, colourful
    L.tileLayer('https://mt1.google.com/vt/lyrs=r&x={x}&y={y}&z={z}', {
      maxZoom: 20,
    }).addTo(map)

    L.control.zoom({ position: 'bottomright' }).addTo(map)

    map.on('click', (e: L.LeafletMouseEvent) => {
      placeMarker(map, e.latlng.lat, e.latlng.lng)
      reverseGeocode(e.latlng.lat, e.latlng.lng)
    })

    if (latitude && longitude) {
      placeMarker(map, latitude, longitude)
    }

    mapRef.current = map

    // Fix leaflet rendering glitch inside dialogs — force recalc after mount
    setTimeout(() => map.invalidateSize(), 200)

    return () => {
      map.remove()
      mapRef.current = null
      markerRef.current = null
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  function placeMarker(map: L.Map, lat: number, lng: number) {
    if (markerRef.current) {
      markerRef.current.setLatLng([lat, lng])
    } else {
      markerRef.current = L.marker([lat, lng], { icon: MARKER_ICON, draggable: true }).addTo(map)
      markerRef.current.on('dragend', () => {
        const pos = markerRef.current!.getLatLng()
        onChange(pos.lat, pos.lng)
        reverseGeocode(pos.lat, pos.lng)
      })
    }
    map.flyTo([lat, lng], Math.max(map.getZoom(), 15), { duration: 0.8 })
  }

  async function reverseGeocode(lat: number, lng: number) {
    onChange(lat, lng)
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18`)
      const data = await res.json()
      if (data.display_name) onChange(lat, lng, data.display_name)
    } catch { /* silent */ }
  }

  async function handleSearch() {
    if (!query.trim()) return
    setSearching(true)
    setError(null)
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`)
      const data = await res.json()
      if (data.length > 0) {
        const lat = parseFloat(data[0].lat)
        const lng = parseFloat(data[0].lon)
        const addr = data[0].display_name ?? query
        if (mapRef.current) placeMarker(mapRef.current, lat, lng)
        onChange(lat, lng, addr)
      } else {
        setError('No results found. Try a different address.')
      }
    } catch {
      setError('Search failed. Check your internet connection.')
    }
    setSearching(false)
  }

  function handleMyLocation() {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser.')
      return
    }
    setLocating(true)
    setError(null)

    // Try high accuracy first, fall back to low accuracy
    navigator.geolocation.getCurrentPosition(
      handleGeoSuccess,
      () => {
        // Retry without high accuracy — works on desktops via IP-based location
        navigator.geolocation.getCurrentPosition(
          handleGeoSuccess,
          handleGeoError,
          { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 },
        )
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 },
    )
  }

  function handleGeoSuccess(pos: GeolocationPosition) {
    const { latitude: lat, longitude: lng } = pos.coords
    if (mapRef.current) placeMarker(mapRef.current, lat, lng)
    reverseGeocode(lat, lng)
    setLocating(false)
  }

  function handleGeoError(err: GeolocationPositionError) {
    setLocating(false)
    switch (err.code) {
      case err.PERMISSION_DENIED:
        setError('Location blocked. Click the lock icon in your browser address bar and allow location access.')
        break
      case err.POSITION_UNAVAILABLE:
        setError('Could not determine location. Try searching an address instead.')
        break
      case err.TIMEOUT:
        setError('Location request timed out. Try again or search an address.')
        break
      default:
        setError('Could not get your location. Try searching instead.')
    }
  }

  return (
    <Box
      sx={{
        borderRadius: 2.5, overflow: 'hidden',
        border: '1px solid', borderColor: 'divider',
        // Override leaflet's default styles for a cleaner look
        '& .leaflet-container': {
          fontFamily: 'inherit',
          background: (t) => t.palette.mode === 'light' ? '#f8f8f8' : '#1a1a2e',
        },
        '& .leaflet-control-zoom': {
          border: 'none !important',
          boxShadow: '0 2px 8px -2px rgba(0,0,0,0.2) !important',
          borderRadius: '8px !important',
          overflow: 'hidden',
        },
        '& .leaflet-control-zoom a': {
          width: '32px !important',
          height: '32px !important',
          lineHeight: '32px !important',
          fontSize: '16px !important',
          color: (t) => t.palette.mode === 'light' ? '#333' : '#eee',
          bgcolor: 'background.paper',
          borderColor: 'divider',
        },
      }}
    >
      {/* Search bar */}
      <Stack direction="row" spacing={1} sx={{ px: 1.5, py: 1.25, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
        <TextField
          value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Search address, city, or place…"
          size="small" fullWidth
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch() } }}
          sx={{ '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: 1.5, '& fieldset': { borderColor: 'divider' } } }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} /></InputAdornment>,
            endAdornment: searching ? <CircularProgress size={16} /> : null,
          }}
        />
        <Tooltip title="Use my location" arrow>
          <IconButton size="small" onClick={handleMyLocation} disabled={locating}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.25, color: 'text.secondary', width: 36, height: 36, flexShrink: 0,
              '&:hover': { color: 'primary.main', borderColor: 'primary.main' } }}>
            {locating ? <CircularProgress size={16} /> : <MyLocationIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Error banner */}
      {error && (
        <Stack direction="row" alignItems="center" spacing={1}
          sx={{ px: 2, py: 1, bgcolor: 'rgba(220,38,38,0.06)', borderBottom: '1px solid', borderColor: 'rgba(220,38,38,0.15)' }}>
          <Typography sx={{ fontSize: 12.5, color: '#DC2626', flex: 1 }}>{error}</Typography>
          <IconButton size="small" onClick={() => setError(null)} sx={{ color: '#DC2626', width: 22, height: 22 }}>
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Stack>
      )}

      {/* Map */}
      <Box ref={containerRef} sx={{ height, cursor: 'crosshair' }} />

      {/* Coordinates */}
      {latitude != null && longitude != null && (
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={2}
          sx={{ px: 2, py: 1, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary', fontFamily: 'ui-monospace, monospace', fontVariantNumeric: 'tabular-nums' }}>
            {latitude.toFixed(6)}, {longitude.toFixed(6)}
          </Typography>
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
            Click map or drag pin to adjust
          </Typography>
        </Stack>
      )}
    </Box>
  )
}
